import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import { useHubExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { resolveTabs } from '../../i18n/resolveTabs';
import PayrollMassPlanPage from './PayrollMassPlanPage';
import HrSynthesisPage from './HrSynthesisPage';
import ForecastSocialTab from '../ForecastSocialTab';
import PlanningTrajectoryPage from './PlanningTrajectoryPage';
import PayrollEvolutionPage from './PayrollEvolutionPage';

/* Planification du personnel — hub à onglets (gabarit unique PageShell + Tabs).
     • Effectifs        (contrats + mouvements hypothétiques → masse brute)
     • Masse salariale  (brut planifié × taux de charges → net / coût employeur)
   Année partagée (globale) portée par le PageHeader (cluster contexte de
   reporting à droite du titre) — pas de picker local pour éviter le doublon. */
/* Ordre = HORIZON qui s'élargit : les effectifs de N et N+1 (nominatif,
   mensuel), puis la trajectoire au-delà (agrégée par département, annuelle),
   puis les montants qu'elles produisent, et enfin la trajectoire chiffrée
   N-1 → N+5.
   L'évolution pluriannuelle revient ICI le 2026-08-26 : elle vivait en
   Chiffres clés, qui lit le RÉEL, alors qu'elle projette cinq ans avec les
   taux de N. Son PDF était déjà rattaché à ce hub. */
/* (Trajectoire et Évolution pluriannuelle ont fondé la section
   « Trajectoire » le 2026-08-30 : elles regardent au-delà de l'exercice, quand
   les deux autres le chiffrent. Le composant reste unique, `only` le
   restreint.) */
const TAB_DEFS = [
  /* (« Planning des effectifs » est parti fonder la section « Effectif » le
     2026-08-27 : un effectif se compare à un effectif.) */
  { id: 'trajectory', labelKey: 'people:planning.tabs.trajectory' },
  { id: 'payroll',    labelKey: 'people:planning.tabs.payroll' },
  { id: 'social',     labelKey: 'people:planning.tabs.social' },
  /* LE DÉTAIL PAR PERSONNE arrive ici le 2026-09-18, en dernier : on va du
     global au nominatif, et c'est le seul onglet qui nomme les gens. Il
     zoomait sur une section qui regarde l'année d'ensemble ; il appartient à
     l'écran où l'on chiffre. */
  { id: 'detail',     labelKey: 'people:synthese.tabs.detail' },
  { id: 'multiyear',  labelKey: 'people:planning.tabs.multiyear' },
];

export default function HrPlanningHub({ activeTenant, setPage, addToast, year, onYearChange, onShowPlanningPrint, only = null, title = null }) {
  const { t } = useTranslation();
  /* `only` : le hub sert alors une SECTION réduite à cette liste d'onglets
     (2026-08-30). « Trajectoire » regarde LOIN (par département, à l'année) ;
     « Prévisionnel » chiffre l'exercice. Deux horizons, deux sections, un seul
     composant. */
  const keep = only == null ? null : (Array.isArray(only) ? only : [only]);
  const defs = keep ? TAB_DEFS.filter(d => keep.includes(d.id)) : TAB_DEFS;
  const [tab, setTab] = useState((keep && keep[0]) || 'trajectory');
  React.useEffect(() => { if (keep && !keep.includes(tab)) setTab(keep[0]); }, [keep, tab]);
  // Unité d'affichage partagée avec Personnel actuel (même préférence par tenant).
  const [unit, setUnit] = usePersistentValue(`people_unit_${activeTenant?.id || ''}`, 'chf');
  /* L'unité monte à la BANDE DE CONTEXTE, comme dans Finance (2026-09-18) :
     elle tenait la droite de la ligne des onglets, où elle changeait de place
     d'un onglet à l'autre. Le gabarit la place, la page la déclare. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const TABS = resolveTabs(defs, t);

  // Rendu d'un onglet — réutilisé à l'écran (unité du hub) et pour l'export Excel
  // (unité forcée à CHF).
  const renderTab = (id, forExport = false) => {
    const u = forExport ? 'chf' : unit;
    switch (id) {
      case 'trajectory': return <PlanningTrajectoryPage embedded unit={u} activeTenant={activeTenant} year={year} addToast={addToast} />;
      case 'detail':    return <HrSynthesisPage embedded unit={u} activeTenant={activeTenant} year={year} onYearChange={onYearChange} />;
      case 'payroll':   return <PayrollMassPlanPage embedded unit={u} activeTenant={activeTenant} year={year} onYearChange={onYearChange} />;
      case 'social':    return <ForecastSocialTab activeTenant={activeTenant} year={year} unit={u} />;
      case 'multiyear': return <PayrollEvolutionPage embedded unit={u} activeTenant={activeTenant} year={year} onYearChange={onYearChange} />;
      default: return null;
    }
  };

  // Exports de la page → bouton du coin bas-gauche (registre pageExports).
  const excel = useHubExcelExport({
    tabs: TABS.map((tb) => ({ label: tb.label, render: () => renderTab(tb.id, true) })),
    fileBase: title || 'Planification personnel',
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  const exportOptions = React.useMemo(() => [
    { id: 'excel', label: 'Excel', run: excel.run },
    ...(onShowPlanningPrint ? [{ id: 'pdf', label: 'PDF', run: () => onShowPlanningPrint(true) }] : []),
  // eslint-disable-next-line
  ], [activeTenant?.id, year, onShowPlanningPrint]);
  useRegisterPageExports(exportOptions);


  return (
    <PageShell
      framed
      kind="report"
      unit={pageUnit}
      title={title || t('people:planning.title')}
    >
      <Tabs
        tabs={TABS}
        active={tab}
        onChange={setTab}
      />

      <div className="page-shell__panel">
        {renderTab(tab)}
      </div>
      {excel.stage}
    </PageShell>
  );
}
